class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Nosto's own script reads the URL's query string on mount and syncs it
    // into this field, then treats "has a value" as "show the dropdown" even
    // though the user never typed anything — noticeable on landing on
    // /search?q=... . That sync happens asynchronously once Nosto's script
    // initializes, later than page load events, so watch for it directly and
    // clear it back to empty (and notify Nosto of the clear) whenever it
    // happens while the field isn't focused.
    this.clearStaleValue();
    this.watchForStaleValue();

    this.setupEventListeners();
    this.refreshCategoriesOnPageLoad();
  }

  clearStaleValue() {
    if (!this.input || this.contains(document.activeElement) || !this.input.value) return;
    this.input.value = '';
    this.notifyNativeInputListeners();
  }

  watchForStaleValue() {
    const target = this.closest('.nosto-autocomplete-wrapper') || this;
    const observer = new MutationObserver(() => this.clearStaleValue());
    observer.observe(target, { childList: true, subtree: true, attributes: true });
    // Nosto's initial sync happens shortly after mount; no need to watch indefinitely.
    setTimeout(() => observer.disconnect(), 10000);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    document.querySelector('.predictive-close-button').addEventListener('click', () => {
      this.close(true);
      if (this.isInsideNostoWrapper()) this.notifyNativeInputListeners();
    })

    // Close the widget (and notify Nosto) on clicks outside it entirely
    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      if (!this.searchTerm && !this.hasAttribute('open') && !this.hasAttribute('results')) return;
      this.close(true);
      if (this.isInsideNostoWrapper()) this.notifyNativeInputListeners();
    });

    // Handle Nosto autocomplete wrapper with input event
    let inputTimeout;
    document.addEventListener('input', (event) => {
      if (event.target.matches('[data-nosto-element="search-input"], .nosto-autocomplete-wrapper #Search-In-Modal-1')) {
        clearTimeout(inputTimeout);
        // Ignore empty-value input events (e.g. our own close-time notifications)
        // so clearing/closing doesn't re-open the box or re-reveal categories.
        if (!event.target.value.trim().length) return;
        document.querySelector('body').classList.add('predictive-search-open');
        this.refreshCategoriesOnPageLoad();
      }
    });
    document.addEventListener('focusin', (event) => {
      if (event.target.matches('.nosto-autocomplete-wrapper #Search-In-Modal-1')) {
        document.querySelector('body').classList.add('predictive-search-open');

        clearTimeout(inputTimeout);
      }
    });

    // Remove class when focus leaves the Nosto wrapper
    document.addEventListener('focusout', (e) => {
      if (e.target.matches('.nosto-autocomplete-wrapper')) {
        document.querySelector('body').classList.remove('predictive-search-open');
      }
    }, true);
  }

  refreshCategoriesOnPageLoad() {
    // Wait for the page to be fully loaded, then update categories
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.syncNostoCategoriesFromHiddenContent();
        this.updateCategoriesForCurrentBrand();
      });
    } else {
      // Page is already loaded
      this.syncNostoCategoriesFromHiddenContent();
      this.updateCategoriesForCurrentBrand();
    }
  }

  syncNostoCategoriesFromHiddenContent() {
    const hidden = document.querySelector('.predictive-search__pages-wrapper.categories .popular-categories_wrapper');
    const html = hidden?.innerHTML || window.latestPopularCategoriesHtml || '';
    if (!html) return;

    this.applyNostoCategories(html);
    this.observeNostoTarget(html);
  }

  applyNostoCategories(html) {
    if (!html) return false;

    const targets = document.querySelectorAll('[data-nosto-autocomplete] .popular-categories_wrapper');
    if (!targets.length) return false;

    targets.forEach((target) => {
      target.innerHTML = html;
      if (target.parentElement) {
        target.parentElement.style.display = '';
      }
    });

    return true;
  }

  observeNostoTarget(html) {
    if (!html) return;
    if (this.applyNostoCategories(html)) return;

    const observer = new MutationObserver(() => {
      if (this.applyNostoCategories(html)) {
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  updateCategoriesForCurrentBrand() {
    // Get the current brand from URL or localStorage
    const urlBrand = new URL(window.location.href).searchParams.get('store_brand');
    const currentBrand = urlBrand || localStorage.getItem('store_brand') || 'metagenics';
    
    if (currentBrand) {
      // First, update localStorage to match the URL brand
      if (urlBrand && urlBrand !== localStorage.getItem('store_brand')) {
        localStorage.setItem('store_brand', urlBrand);
      }
      
      // Update cart attributes to ensure server-side rendering uses the correct brand
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { store_brand: currentBrand } })
      }).then(() => {
        // Now fetch the correct categories for this brand
        this.fetchAndUpdateCategories(currentBrand);
      });
    }
  }

  async fetchAndUpdateCategories(brand) {
    try {
      // Fetch the home page with the correct brand to get the categories
      const response = await fetch(`/?store_brand=${encodeURIComponent(brand)}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });
      
      if (response.ok) {
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const categoriesSection = doc.querySelector('.predictive-search__pages-wrapper.categories');
        const fetched = categoriesSection?.querySelector('.popular-categories_wrapper')?.innerHTML;
        
        if (categoriesSection && fetched) {
          const existingCategoriesSection = document.querySelector('.predictive-search__pages-wrapper.categories');
          if (existingCategoriesSection) {
            existingCategoriesSection.innerHTML = categoriesSection.innerHTML;
          }

          window.latestPopularCategoriesHtml = fetched;
          this.applyNostoCategories(fetched);
          this.observeNostoTarget(fetched);
        }
      }
    } catch (error) {
      console.error('Error fetching brand categories:', error);
    }
  }


  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    document.querySelector('body').classList.add('predictive-search-open');
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }

  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const customerType = window.Shopify?.customer_type || '';
    const isB2B = ['practitioner', 'student', 'retailer'].includes(customerType.toLowerCase());
  
    const baseTerm = (searchTerm || '').trim();
    if (!baseTerm) return;
  
    // Get current brand from the existing system
    const brand = !isB2B ? getBrandFromLocalStorage() : '';
    const storeBrand = new URLSearchParams(window.location.search).get('store_brand') || 
                      localStorage.getItem('store_brand') || 
                      'metagenics';

    const effectiveTerm = baseTerm;
    // Create cache key based on search term, brand, and store
    const queryKey = `${effectiveTerm.toLowerCase().replace(/\s+/g, '-')}-${brand || 'all'}-${storeBrand}`;
  
    this.setLiveRegionLoadingState();
  
    // Clear any existing results first to prevent stale data
    this.predictiveSearchResults.innerHTML = '';
    
    // Skip cache for now to ensure fresh results on every search
    // if (this.cachedResults[queryKey]) {
    //   this.renderSearchResults(this.cachedResults[queryKey]);
    //   return;
    // }
  
    // Use search API with proper Shopify filter syntax
    let searchUrl = `/search?q=${encodeURIComponent(effectiveTerm)}&type=product&options[prefix]=last`;
    
    // Add vendor filtering for non-B2B users using Shopify's filter syntax
    if (!isB2B && brand) {
      searchUrl += `&filter.p.vendor=${encodeURIComponent(brand)}`;
    }
  
    
    fetch(searchUrl, { signal: this.abortController.signal })
      .then(r => { 
        if (!r.ok) { 
          this.close(); 
          throw new Error(r.status); 
        } 
        return r.text(); 
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
  
        // Debug: Log the HTML structure
        
        // Debug: Check if the search actually found results
        const hasResults = html.includes('product-grid') || html.includes('grid__item') || html.includes('product-item');
        
        // Extract products from search results - look specifically in the search results section
        let productItems = [];
        
        // Look for the main search results container first
        const searchResultsSection = doc.querySelector('.search-results, .template-search__results, .product-grid, .collection');
        
        if (searchResultsSection) {
          
          // Look for products within the search results section
          productItems = searchResultsSection.querySelectorAll('.grid__item, .product-item, .card, .product-card, .collection__item, .product-grid__item, [data-product-item], li');
          
          // If still no products, try filtering li elements within the search results
          if (productItems.length === 0) {
            const allLis = searchResultsSection.querySelectorAll('li');
            
            productItems = Array.from(allLis).filter(li => {
              const link = li.querySelector('a[href*="/products/"]');
              const title = li.querySelector('h3, h4, .card__heading, .card__title, .product-title, .product-name');
              const hasProductLink = !!link;
              const hasTitle = !!title;
              if (hasProductLink && hasTitle) {
              }
              return hasProductLink && hasTitle;
            });
          }
        } else {
          console.log('No search results section found, falling back to document-wide search...');
          // Fallback: search the entire document but be more restrictive
          const allLis = doc.querySelectorAll('li');
          console.log('Total li elements in document:', allLis.length);
          
          productItems = Array.from(allLis).filter(li => {
            const link = li.querySelector('a[href*="/products/"]');
            const title = li.querySelector('h3, h4, .card__heading, .card__title, .product-title, .product-name');
            const hasProductLink = !!link;
            const hasTitle = !!title;
            if (hasProductLink && hasTitle) {
              console.log('Found product li in document:', { linkHref: link?.href, titleText: title?.textContent?.trim() });
            }
            return hasProductLink && hasTitle;
          });
        }
        
        // Remove duplicates by checking for unique product links
        const uniqueProducts = [];
        const seenLinks = new Set();
        
        productItems.forEach(item => {
          const link = item.querySelector('a[href*="/products/"]');
          if (link && !seenLinks.has(link.href)) {
            seenLinks.add(link.href);
            uniqueProducts.push(item);
          }
        });
        
        productItems = uniqueProducts;
        console.log('Found product items (after deduplication):', productItems.length);
        
        // Debug: Check if we're missing products due to pagination or other issues
        const totalProductsInHTML = doc.querySelectorAll('a[href*="/products/"]').length;
        console.log('Total product links in entire HTML:', totalProductsInHTML);
        
        // Check if there are pagination indicators
        const pagination = doc.querySelector('.pagination, .pager, [class*="page"], [class*="pagination"]');
        console.log('Pagination found:', !!pagination);
        
        // Check for "Load More" or similar buttons
        const loadMoreButton = doc.querySelector('[class*="load"], [class*="more"], button[class*="load"], button[class*="more"]');
        console.log('Load more button found:', !!loadMoreButton);
        
        // Debug: Log the actual HTML structure we're getting
        if (productItems.length === 0) {
          console.log('No product items found. Checking HTML structure...');
          console.log('HTML contains "template-search__results":', html.includes('template-search__results'));
          console.log('HTML contains "grid__item":', html.includes('grid__item'));
          console.log('HTML contains "product-item":', html.includes('product-item'));
          console.log('HTML contains "search-results":', html.includes('search-results'));
          
          // Try to find any list items or grid items
          const allListItems = doc.querySelectorAll('li');
          const allGridItems = doc.querySelectorAll('[class*="grid"]');
          console.log('Found list items:', allListItems.length);
          console.log('Found grid items:', allGridItems.length);
        } else {
          // Debug: Check if we're picking up the right products
          console.log('First few product titles:');
          Array.from(productItems).slice(0, 3).forEach((item, index) => {
            const title = item.querySelector('.product-item__title, .card__heading, .card__title, h3, h4, .product-title, .product-name')?.textContent?.trim() || 'No title';
            const vendor = item.querySelector('.product-item__vendor, .card__vendor, .product-vendor, .vendor')?.textContent?.trim() || 'No vendor';
            console.log(`  ${index + 1}. ${title} (${vendor})`);
          });
        }
        
        // Debug: Log first few product titles to see what's being returned
        if (productItems.length > 0) {
          const productTitles = Array.from(productItems).slice(0, 3).map(item => {
            const title = item.querySelector('.product-item__title, .card__heading, .card__title, h3, h4, .product-title, .product-name')?.textContent?.trim() || 'No title';
            const vendor = item.querySelector('.product-item__vendor, .card__vendor, .product-vendor, .vendor')?.textContent?.trim() || 'No vendor';
            return { title, vendor };
          });
          
          console.log('First 3 products:', productTitles);
          
          // Check if any product titles contain the search term
          const searchTermLower = effectiveTerm.toLowerCase();
          const hasMatchingProducts = productTitles.some(p => p.title.toLowerCase().includes(searchTermLower));
          console.log('Any products contain search term "' + effectiveTerm + '":', hasMatchingProducts);
          
              // Reject results if no products actually match the search term
              // if (!hasMatchingProducts && effectiveTerm.length > 2) {
              //   console.log('No products match search term - showing no results');
              //   const noResultsMarkup = this.buildNoResultsMarkup(effectiveTerm);
              //   this.renderSearchResults(noResultsMarkup);
              //   return;
              // }
        }
        
        if (productItems.length > 0) {
          // Build predictive search markup from search results
          const resultsMarkup = this.buildPredictiveSearchMarkup(productItems, effectiveTerm);
  
        this.allPredictiveSearchInstances.forEach(inst => {
          inst.cachedResults[queryKey] = resultsMarkup;
        });
  
        this.renderSearchResults(resultsMarkup);
        } else {
          // Clear any existing results and show no results message, but still include popular categories
          console.log('No products found for search term:', effectiveTerm);
          const noResultsMarkup = this.buildNoResultsMarkup(effectiveTerm);
          
          // Clear cache for this query to prevent stale results
          delete this.cachedResults[queryKey];
          
          this.renderSearchResults(noResultsMarkup);
        }
      })
      .catch(err => { 
        if (err?.code !== 20) {
          // Show error message instead of fallback
          const errorMarkup = this.buildErrorMarkup(effectiveTerm);
          this.renderSearchResults(errorMarkup);
        }
      });
  }

  buildPredictiveSearchMarkup(productItems, searchTerm) {
    let markup = '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">';
    markup += '<div class="predictive-search__result-group">';
    markup += '<div>';
    markup += '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>';
    markup += '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">';
    
    // Limit to 8 products for predictive search
    const limitedItems = Array.from(productItems).slice(0, 8);
    
    console.log('Building markup for', limitedItems.length, 'products');
    
        limitedItems.forEach((item, index) => {
          // Try multiple selectors for different theme layouts
          const link = item.querySelector('a') || item.closest('a');
          const title = item.querySelector('.product-item__title, .card__heading, .card__title, h3, h4, .product-title, .product-name, .card__heading a, .card__title a')?.textContent?.trim() || 
                       item.querySelector('a')?.getAttribute('aria-label') || 
                       item.querySelector('a')?.textContent?.trim() || 'Product';
          const image = item.querySelector('img');
          const priceElement = item.querySelector('.price, .money, .price-item, .product-price, .card__price');
          const vendor = item.querySelector('.product-item__vendor, .card__vendor, .product-vendor, .vendor, .card__vendor')?.textContent?.trim() || '';
          
          // Clean up price formatting
          let cleanPrice = '';
          if (priceElement) {
            const priceText = priceElement.textContent?.trim() || '';
            // Extract just the price amount (e.g., "$32.50" from "Regular price $32.50")
            const priceMatch = priceText.match(/\$[\d,]+\.?\d*/);
            if (priceMatch) {
              cleanPrice = priceMatch[0];
            }
          }
          
          console.log(`Product ${index + 1}:`, { title, vendor, hasLink: !!link, hasImage: !!image, cleanPrice });
          
          if (link) {
            markup += `<li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
            markup += `<a href="${link.href}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">`;
            
            if (image) {
              markup += `<img class="predictive-search__image" src="${image.src}" alt="${image.alt || title}" width="50" height="50">`;
            }
            
            markup += '<div class="predictive-search__item-content">';
            if (vendor) {
              markup += `<div class="predictive-search__item-vendor caption-with-letter-spacing">${vendor}</div>`;
            }
            markup += `<p class="predictive-search__item-heading h5">${title}</p>`;
            if (cleanPrice) {
              markup += `<div class="predictive-search__item-price">${cleanPrice}</div>`;
            }
            markup += '</div></a></li>';
          }
        });
    
    markup += '</ul></div></div></div>';
    
    // Add popular categories section (if not B2B) - always add, even with no results
    markup = this.addPopularCategoriesToMarkup(markup);
    
    // Add search for button
    markup += `<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
      <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
        <span data-predictive-search-search-for-text>Search for <span class="query">"${searchTerm}"</span></span>
        <svg class="icon icon-arrow" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 6">
          <path fill-rule="evenodd" clip-rule="evenodd" d="m9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708Z"/>
        </svg>
      </button>
    </div>`;
    
    // Add live region count for accessibility
    markup += `<span class="hidden" data-predictive-search-live-region-count-value>
      ${limitedItems.length} product${limitedItems.length !== 1 ? 's' : ''} found for "${searchTerm}"
    </span>`;
    
    return markup;
  }

  addPopularCategoriesToMarkup(markup) {
    // Add popular categories section (if not B2B) - always add, even with no results
    const isB2B = window.Shopify?.customer_type === 'b2b';
    
    if (!isB2B) {
      // Get the existing popular categories section from the page
      const existingCategoriesSection = document.querySelector('.predictive-search__pages-wrapper.categories');
      
      if (existingCategoriesSection) {
        // Remove the display: none style and add to markup
        const categoriesHTML = existingCategoriesSection.outerHTML.replace('style="display: none;"', '');
        markup += categoriesHTML;
      }
    }
    
    return markup;
  }

  buildNoResultsMarkup(searchTerm) {
    let markup = '';
    
    // Add popular categories section (if not B2B) - always add, even with no results
    markup = this.addPopularCategoriesToMarkup(markup);
    
    // Add search for button
    markup += `<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
      <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
        <span data-predictive-search-search-for-text>Search for <span class="query">"${searchTerm}"</span></span>
        <svg class="icon icon-arrow" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 6">
          <path fill-rule="evenodd" clip-rule="evenodd" d="m9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708Z"/>
        </svg>
      </button>
    </div>`;
    
    return markup;
  }

  buildErrorMarkup(searchTerm) {
    let markup = '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">';
    markup += '<div class="predictive-search__result-group">';
    markup += '<div>';
    markup += '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>';
    markup += '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">';
    markup += '<li class="predictive-search__list-item"><p class="predictive-search__item-heading h5">Search temporarily unavailable</p></li>';
    markup += '</ul></div></div></div>';
    
    // Add search for button
    markup += `<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
      <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
        <span data-predictive-search-search-for-text>Search for <span class="query">"${searchTerm}"</span></span>
        <svg class="icon icon-arrow" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 6">
          <path fill-rule="evenodd" clip-rule="evenodd" d="m9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708Z"/>
        </svg>
      </button>
    </div>`;
    
    return markup;
  }  

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionElement) {
      this.setLiveRegionText(liveRegionElement.textContent);
    } else {
      // Fallback if live region element doesn't exist
      this.setLiveRegionText('Search results loaded');
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.querySelector('body').classList.add('predictive-search-open');
    
    // Ensure the predictive search container is visible
    const predictiveSearchContainer = this.querySelector('.predictive-search');
    if (predictiveSearchContainer) {
      predictiveSearchContainer.style.display = 'block';
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    // Don't remove class if focus is on Nosto input
    const activeElement = document.activeElement;
    if (!activeElement?.matches('#Search-In-Modal-1')) {
      document.querySelector('body').classList.remove('predictive-search-open');
      const nostoWrapper = this.input?.closest('.nosto-autocomplete-wrapper');
      if (nostoWrapper?.hasAttribute('style')) nostoWrapper.removeAttribute('style');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

const BRAND_MAP = {
  'metagenics': 'Metagenics',
  'ethical-nutrients': 'Ethical Nutrients',
  'inner-health': 'Inner Health',
};

function getBrandFromLocalStorage() {
  const slug = (localStorage.getItem('store_brand') || '').trim().toLowerCase();
  return BRAND_MAP[slug] || '';
}

function termIncludesBrand(term, brand) {
  if (!brand) return false;
  const re = new RegExp(`\\b${brand.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i');
  return re.test(term);
}

class SearchInstitute extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('search-institute');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Nosto's own script reads the URL's query string on mount and syncs it
    // into this field, then treats "has a value" as "show the dropdown" even
    // though the user never typed anything — noticeable on landing on
    // /search?q=... . That sync happens asynchronously once Nosto's script
    // initializes, later than page load events, so watch for it directly and
    // clear it back to empty (and notify Nosto of the clear) whenever it
    // happens while the field isn't focused.
    this.clearStaleValue();
    this.watchForStaleValue();

    this.setupEventListeners();
  }

  clearStaleValue() {
    if (!this.input || this.contains(document.activeElement) || !this.input.value) return;
    this.input.value = '';
    this.notifyNativeInputListeners();
  }

  watchForStaleValue() {
    const target = this.closest('.nosto-autocomplete-wrapper') || this;
    const observer = new MutationObserver(() => this.clearStaleValue());
    observer.observe(target, { childList: true, subtree: true, attributes: true });
    // Nosto's initial sync happens shortly after mount; no need to watch indefinitely.
    setTimeout(() => observer.disconnect(), 10000);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    document.querySelector('.predictive-close-button').addEventListener('click', () => {
      this.close(true);
      if (this.isInsideNostoWrapper()) this.notifyNativeInputListeners();
    })

    // Close the widget (and notify Nosto) on clicks outside it entirely
    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      if (!this.searchTerm && !this.hasAttribute('open') && !this.hasAttribute('results')) return;
      this.close(true);
      if (this.isInsideNostoWrapper()) this.notifyNativeInputListeners();
    });

    // Handle Nosto autocomplete wrapper with input event
    let inputTimeout;
    document.addEventListener('input', (event) => {
      if (event.target.matches('.nosto-autocomplete-wrapper #Search-In-Modal-1')) {
        clearTimeout(inputTimeout);
        // Ignore empty-value input events (e.g. our own close-time notifications)
        // so clearing/closing doesn't re-open the box.
        if (!event.target.value.trim().length) return;
        document.querySelector('body').classList.add('predictive-search-open');
      }
    });
    document.addEventListener('focusin', (event) => {
      if (event.target.matches('.nosto-autocomplete-wrapper #Search-In-Modal-1')) {
        document.querySelector('body').classList.add('predictive-search-open');

        clearTimeout(inputTimeout);
      }
    });

    // Remove class when focus leaves the Nosto wrapper
    document.addEventListener('focusout', (e) => {
      if (e.target.matches('.nosto-autocomplete-wrapper')) {
        document.querySelector('body').classList.remove('predictive-search-open');
      }
    }, true);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    document.querySelector('body').classList.add('predictive-search-open');
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }

  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
        (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const query = searchTerm.trim();
    if (!query) return;

    const queryKey = `articles:${query.toLowerCase()}`;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url = `/search?type=article&q=${encodeURIComponent(query)}`;

    fetch(url, { signal: this.abortController.signal })
        .then(r => {
          if (!r.ok) {
            this.close();
            throw new Error(r.status);
          }
          return r.text();
        })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const container = doc.querySelector(`#predictive-search-results-groups-wrapper`);
          const resultsMarkup = container ? container.innerHTML : '';

          // расшариваем кэш на все инстансы
          this.allPredictiveSearchInstances.forEach(inst => {
            inst.cachedResults[queryKey] = resultsMarkup;
          });

          this.renderSearchResults(resultsMarkup);
        })
        .catch(err => { if (err?.code !== 20) this.close(); });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
        window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.querySelector('body').classList.add('predictive-search-open');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    // Don't remove class if focus is on Nosto input
    const activeElement = document.activeElement;
    if (!activeElement?.matches('#Search-In-Modal-1')) {
      document.querySelector('body').classList.remove('predictive-search-open');
      const nostoWrapper = this.input?.closest('.nosto-autocomplete-wrapper');
      if (nostoWrapper?.hasAttribute('style')) nostoWrapper.removeAttribute('style');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('search-institute', SearchInstitute);
